import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';

import { LoadingType, setLoadingState } from 'prequalificationV2/actions';
import { MissingEmploymentInformation as MissingEmploymentInformationItem } from 'prequalificationV2/api/ApplicationApi';
import { hilEmploymentsApi, MissingEmploymentInformationDto } from 'api/EmploymentsApi';
import { Button } from 'components/Buttons/Button';
import Input from 'components/Inputs/Input';
import NumberInput from 'components/Inputs/NumberInput';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import { SearchLocationInput } from 'components/SearchLocationInput';
import { BorrowerRole } from 'enums/BorrowerRole';
import { DashboardTabName } from 'enums/DashboardTabName';
import { DateFormat } from 'enums/DateFormat';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import {
  EMPLOYMENT_ADDRESS_VARIABLES,
  MAP_INCOME_TYPE_TO_EMPLOYMENT_SECTION_TITLE,
  MAP_INCOME_TYPE_TO_EMPLOYMENT_VARIABLE_LABELS,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.constants';
import { CurrentEmploymentVariable } from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.enums';
import {
  getEmploymentVariableName,
  getEmploymentVariableValue,
  getIsMissingEmploymentInformationCompleted,
  getMissingEmploymentInformation,
  getMissingEmploymentInformationDefaultValues,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.utils';
import styles from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/InformationTabs.module.scss';
import { isValidDateBeforeNow, parseMonthYearToDate } from 'prequalificationV2/utils/dateUtils';
import { addressRegistration } from 'prequalificationV2/utils/registrationUtils';
import {
  getErrorMessageForRequiredFields,
  getStreetNameFromStreetAddress,
  getStreetNumberFromStreetAddress,
} from 'prequalificationV2/utils/stringUtils';
import { updateApplicationInStore } from 'prequalificationV2/utils/updateApplicationInStore';
import { getMessageForInvalidFields, validatePhoneNumber } from 'prequalificationV2/utils/validationUtils';

interface MissingEmploymentInformationProps {
  borrowerType: BorrowerRole;
}

const MissingEmploymentInformation = ({ borrowerType }: MissingEmploymentInformationProps) => {
  const dispatch = useAppDispatch();

  const { variables, applicationId, dashboardAdditionalInfo } = useAppSelector((state) => state.application);
  const applicationVariables = variables!;
  const missingEmploymentInformationList = getMissingEmploymentInformation(dashboardAdditionalInfo!, borrowerType);
  const isCompleted = getIsMissingEmploymentInformationCompleted(missingEmploymentInformationList);

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    watch,
    trigger,
    control,
    getValues,
    formState,
    formState: { errors },
  } = useForm({
    defaultValues: getMissingEmploymentInformationDefaultValues(
      applicationVariables,
      missingEmploymentInformationList,
      borrowerType,
    ),
    shouldUnregister: false,
  });
  const watcher = watch();

  const getEmploymentVariables = (missingEmploymentInformation: MissingEmploymentInformationItem) => {
    return Object.keys(
      MAP_INCOME_TYPE_TO_EMPLOYMENT_VARIABLE_LABELS[missingEmploymentInformation.incomeType] || {},
    ) as CurrentEmploymentVariable[];
  };

  const getEmploymentVariableLabel = (
    variable: CurrentEmploymentVariable,
    missingEmploymentInformation: MissingEmploymentInformationItem,
  ) => {
    return MAP_INCOME_TYPE_TO_EMPLOYMENT_VARIABLE_LABELS[missingEmploymentInformation.incomeType]?.[variable] || '';
  };

  const getIsVariableEditable = (
    variable: CurrentEmploymentVariable,
    missingEmploymentInformation: MissingEmploymentInformationItem,
  ) => {
    return !getEmploymentVariableValue(
      applicationVariables,
      variable,
      borrowerType,
      missingEmploymentInformation.index,
    );
  };

  const getIsAddressEditable = (missingEmploymentInformation: MissingEmploymentInformationItem) => {
    return getEmploymentVariables(missingEmploymentInformation).some(
      (variable) =>
        EMPLOYMENT_ADDRESS_VARIABLES.includes(variable) &&
        getIsVariableEditable(variable, missingEmploymentInformation),
    );
  };

  useEffect(() => {
    if (isCompleted) {
      return;
    }

    missingEmploymentInformationList.forEach((missingEmploymentInformation) => {
      const { index } = missingEmploymentInformation;
      const employmentVariables = getEmploymentVariables(missingEmploymentInformation);

      employmentVariables
        .filter(
          (variable) =>
            getIsVariableEditable(variable, missingEmploymentInformation) &&
            !EMPLOYMENT_ADDRESS_VARIABLES.includes(variable),
        )
        .forEach((variable) => {
          const label = getEmploymentVariableLabel(variable, missingEmploymentInformation);

          register(getEmploymentVariableName(variable, borrowerType, index), {
            required: { value: true, message: getErrorMessageForRequiredFields(label) },
            validate: (value) => {
              switch (variable) {
                case CurrentEmploymentVariable.EmployerPhone:
                  return validatePhoneNumber(value);
                case CurrentEmploymentVariable.HireDate:
                  return isValidDateBeforeNow(value, DateFormat.MonthYear) || getMessageForInvalidFields(label);
                default:
                  return true;
              }
            },
          });
        });

      if (getIsAddressEditable(missingEmploymentInformation)) {
        addressRegistration(
          register,
          {
            streetName: getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index),
            cityName: getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, index),
            stateName: getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, index),
            zipName: getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, index),
          },
          {
            streetLabel: getEmploymentVariableLabel(CurrentEmploymentVariable.Address, missingEmploymentInformation),
            cityLabel: getEmploymentVariableLabel(CurrentEmploymentVariable.City, missingEmploymentInformation),
            stateLabel: getEmploymentVariableLabel(CurrentEmploymentVariable.State, missingEmploymentInformation),
            zipLabel: getEmploymentVariableLabel(CurrentEmploymentVariable.Zip, missingEmploymentInformation),
          },
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fields are registered once per mount: form defaultValues are mount-only, so re-registering on store updates wouldn't resync the form; isCompleted covers the post-save transition
  }, [register, isCompleted]);

  const saveMissingEmploymentInformation = handleSubmit(async () => {
    const loadingType = `${LoadingType.RequiredInformationUpdating}${DashboardTabName.AdditionalInformation}`;

    try {
      dispatch(setLoadingState({ type: loadingType, loading: true }));

      const updatedApplication = await hilEmploymentsApi.updateMissingEmploymentInformation({
        applicationId: applicationId as string,
        borrowerType: borrowerType,
        employments: missingEmploymentInformationList.map(({ index }) => {
          const streetAddress =
            watcher[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index)];
          const hireDate = watcher[getEmploymentVariableName(CurrentEmploymentVariable.HireDate, borrowerType, index)];

          const missingEmploymentInformationDto: MissingEmploymentInformationDto = {
            index: index,
            name: watcher[getEmploymentVariableName(CurrentEmploymentVariable.EmployerName, borrowerType, index)],
            position: watcher[getEmploymentVariableName(CurrentEmploymentVariable.Position, borrowerType, index)],
            phone: watcher[getEmploymentVariableName(CurrentEmploymentVariable.EmployerPhone, borrowerType, index)],
            hireDate: hireDate ? parseMonthYearToDate(hireDate) : undefined,
            address: {
              streetNumber: streetAddress ? getStreetNumberFromStreetAddress(streetAddress) : undefined,
              streetName: getStreetNameFromStreetAddress(streetAddress),
              city: watcher[getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, index)],
              stateOrProvince: watcher[getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, index)],
              zipOrPostalCode: watcher[getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, index)],
            },
          };

          return missingEmploymentInformationDto;
        }),
      });

      updateApplicationInStore(updatedApplication, dispatch);
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(setLoadingState({ type: loadingType, loading: false }));
    }
  });

  const handleInputChange = (name: string, value: string) => {
    clearErrors(name);
    setValue(name, value);
  };

  const renderInput = (
    variable: CurrentEmploymentVariable,
    missingEmploymentInformation: MissingEmploymentInformationItem,
  ) => {
    const name = getEmploymentVariableName(variable, borrowerType, missingEmploymentInformation.index);
    const label = getEmploymentVariableLabel(variable, missingEmploymentInformation);
    const isDisabled = !getIsVariableEditable(variable, missingEmploymentInformation);

    switch (variable) {
      case CurrentEmploymentVariable.EmployerName:
      case CurrentEmploymentVariable.Position:
        return (
          <Input
            key={name}
            label={label}
            name={name}
            value={watcher[name]}
            errorMessage={errors[name]?.message}
            inputContainerClassName={styles.input}
            disabled={isDisabled}
            onChange={(event) => handleInputChange(name, event.target.value)}
          />
        );
      case CurrentEmploymentVariable.EmployerPhone:
        return (
          <PhoneNumberInput
            key={name}
            label={label}
            name={name}
            className={styles.input}
            inputContainerClassname={styles.input}
            value={watcher[name]}
            errorMessage={errors[name]?.message}
            disabled={isDisabled}
            onChange={(value: string) => handleInputChange(name, value)}
            onBlur={() => {
              if (watcher[name]) {
                trigger(name);
              }
            }}
          />
        );
      case CurrentEmploymentVariable.HireDate:
        return (
          <NumberInput
            key={name}
            label={label}
            name={name}
            value={watcher[name]}
            errorMessage={errors[name]?.message}
            disabled={isDisabled}
            className={styles.input}
            onChange={(event) => handleInputChange(name, event.target.value)}
            onBlur={() => {
              if (watcher[name]) {
                trigger(name);
              }
            }}
            placeholder="MM/YYYY"
            format="##/####"
            mask="_"
            allowEmptyFormatting
          />
        );
      default:
        return null;
    }
  };

  const renderAddress = (missingEmploymentInformation: MissingEmploymentInformationItem) => {
    const hasAddress = getEmploymentVariables(missingEmploymentInformation).some((variable) =>
      EMPLOYMENT_ADDRESS_VARIABLES.includes(variable),
    );

    if (!hasAddress) {
      return null;
    }

    const { index } = missingEmploymentInformation;
    const inputNames = {
      streetAddress: getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index),
      city: getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, index),
      state: getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, index),
      zip: getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, index),
    };

    return (
      <SearchLocationInput
        containerClassName={styles.select_input}
        initialValue={{
          streetAddress: watcher[inputNames.streetAddress],
          city: watcher[inputNames.city],
          state: watcher[inputNames.state],
          zip: watcher[inputNames.zip],
        }}
        streetLabel={getEmploymentVariableLabel(CurrentEmploymentVariable.Address, missingEmploymentInformation)}
        cityLabel={getEmploymentVariableLabel(CurrentEmploymentVariable.City, missingEmploymentInformation)}
        stateLabel={getEmploymentVariableLabel(CurrentEmploymentVariable.State, missingEmploymentInformation)}
        zipLabel={getEmploymentVariableLabel(CurrentEmploymentVariable.Zip, missingEmploymentInformation)}
        control={control}
        useFormControl
        inputNames={inputNames}
        required
        errors={{
          streetAddress: errors[inputNames.streetAddress],
          city: errors[inputNames.city],
          state: errors[inputNames.state],
          zip: errors[inputNames.zip],
        }}
        inputChange={(name, value) => {
          setValue(name, value);

          if (formState.isSubmitted) {
            trigger(name);
          }
        }}
        getValues={getValues}
        disabled={!getIsAddressEditable(missingEmploymentInformation)}
      />
    );
  };

  return (
    <div>
      {missingEmploymentInformationList.map((missingEmploymentInformation) => (
        <div key={missingEmploymentInformation.index} className={styles.infoContainer}>
          <div className={styles.formHeader}>
            {MAP_INCOME_TYPE_TO_EMPLOYMENT_SECTION_TITLE[missingEmploymentInformation.incomeType]}
          </div>
          <div className={styles.employmentContainer}>
            <div className={styles.formRow}>
              {getEmploymentVariables(missingEmploymentInformation)
                .filter((variable) => !EMPLOYMENT_ADDRESS_VARIABLES.includes(variable))
                .map((variable) => renderInput(variable, missingEmploymentInformation))}
            </div>
            {renderAddress(missingEmploymentInformation)}
          </div>
        </div>
      ))}
      {!isCompleted && <Button onClick={saveMissingEmploymentInformation}>Save</Button>}
    </div>
  );
};

export default MissingEmploymentInformation;
